/**
 * 派单模块主页面
 * 结构对齐 OrderListPage：Tabs 切换订单类型，工厂组件按类型渲染不同内容
 */
import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Card, Tabs } from 'antd'
import PageHeader from '@/components/PageHeader'
import {
  fetchOrdersByType,
  ORDER_LIST_REDUX_SCOPE
} from '@/redux/order/maternityCenterOrderSlice'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { clearSelectedOrder } from '@/redux/order/dispatchPageSlice'
import DispatcherContentFactory from './components/DispatcherContentFactory'
import { ORDER_TYPE_LIST } from '../orderList/config/orderTypeConfig'
import './DispatcherPageList.less'

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '订单管理', href: '/client/order/orderList' },
  { breadcrumbName: '派单' }
]

/** 默认 query（月子中心用，其他类型可复用部分字段） */
const getDefaultQuery = () => ({
  storeId: '',
  checkInStatus: 2,
  keyword: '',
  pageNumber: 1,
  pageSize: 20
})

/**
 * 与 getJStoreListSlice.processSingleDataType 一致：接口多为 { value, label }，无 id；
 * 不能对 thunk的 payload.data 直接读 [0].id，否则首店一直是空字符串。
 */
const getFirstStoreIdFromStoreApiData = (raw) => {
  let list = raw
  if (list && !Array.isArray(list) && Array.isArray(list.records)) {
    list = list.records
  }
  if (!Array.isArray(list) || list.length === 0) return ''
  const item = list[0]
  const rawId = item?.id ?? item?.value
  return rawId != null && String(rawId).trim() !== '' ? String(rawId) : ''
}

class DispatcherPageList extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      currentOrderType: 'maternity_center'
    }
  }

  componentDidMount() {
    const { dispatch } = this.props
    /**
     * 派单列表使用 Redux dispatchOrderList，与订单列表 orderListPage 分库；进入本页用默认条件拉单。
     */
    dispatch(fetchJStoreList({ query: {}, dataType: 'optionErp' }))
      .then((action) => {
        const raw = action?.payload?.data
        const list = Array.isArray(raw) ? raw.filter((item) => item.extra === 'erp') : raw
        return getFirstStoreIdFromStoreApiData(list)
      })
      .catch(() => '')
      .then((firstStoreId) => {
        dispatch(clearSelectedOrder())
        this.fetchOrders({
          ...getDefaultQuery(),
          storeId: firstStoreId
        })
      })
  }

  /**
   * @param {Object} uiQuery
   * @param {{ silent?: boolean }} [options] - silent: 后台刷新，不转列表 loading，避免影响右侧操作区
   */
  fetchOrders = (uiQuery, options = {}) => {
    const { dispatch } = this.props
    const { currentOrderType } = this.state
    const { silent = false } = options
    const maternityCenterListMode =
      currentOrderType === 'maternity_center' && String(uiQuery?.checkInStatus) === '1'
        ? 'dispatchUnassigned'
        : 'orderList'
    return dispatch(
      fetchOrdersByType({
        query: uiQuery,
        orderType: currentOrderType,
        maternityCenterListMode,
        orderListScope: ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE,
        silent
      })
    )
  }

  /** 统一刷新列表（无参，使用当前 query）— 供 modal 提交成功后调用 */
  refreshList = () => {
    const query = this.props.orderObj?.dispatchOrderList?.query || getDefaultQuery()
    this.fetchOrders(query)
  }

  handleOrderTypeChange = (orderType) => {
    const { jStoreOptions } = this.props
    this.props.dispatch(clearSelectedOrder())
    this.setState({ currentOrderType: orderType }, () => {
      const query = this.props.orderObj?.dispatchOrderList?.query || getDefaultQuery()
      const firstStoreId = jStoreOptions?.[0]?.id != null ? String(jStoreOptions[0].id) : ''
      const storeId = orderType === 'home_service' ? '98' : (firstStoreId || query.storeId)
      this.fetchOrders({ ...query, pageNumber: 1, storeId })
    })
  }

  handleFilterChange = (mergedQuery) => {
    const prevQuery = this.props.orderObj?.dispatchOrderList?.query || {}
    const prevStoreId = prevQuery.storeId
    const prevCheckIn = prevQuery.checkInStatus
    const nextStoreId = mergedQuery.storeId
    const nextCheckIn = mergedQuery.checkInStatus
    if (prevStoreId !== nextStoreId || prevCheckIn !== nextCheckIn) {
      this.props.dispatch(clearSelectedOrder())
    }
    this.fetchOrders(mergedQuery)
  }
  render() {
    const { currentOrderType } = this.state
    const { orderObj, dispatchPage, jStoreOptions, dispatch } = this.props

    return (
      <div className="dispatcher-page-list">
        <PageHeader breadcrumbList={breadcrumbList} />
        <div className="i2-container">
          <Card className="i2-card-page">
            <Tabs
              activeKey={currentOrderType}
              onChange={this.handleOrderTypeChange}
              type="card"
              items={ORDER_TYPE_LIST.map((type) => ({
                key: type.key,
                label: type.text
              }))}
            />
            <DispatcherContentFactory
              orderType={currentOrderType}
              orderObj={orderObj}
              dispatchPage={dispatchPage}
              jStoreOptions={jStoreOptions}
              dispatch={dispatch}
              fetchOrders={this.fetchOrders}
              refreshList={this.refreshList}
              handleFilterChange={this.handleFilterChange}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const DispatcherPageListWrapper = () => {
  const dispatch = useDispatch()
  const orderState = useSelector((state) => state.order || {})
  const orderObj = orderState.order || {}
  const dispatchPage = orderState.dispatchPage || {}
  const componentsState = useSelector((state) => state.components || {})
  const jStoreListObj = componentsState.getJStoreList || {}
  const jStoreOptions = jStoreListObj.data || []

  return (
    <DispatcherPageList
      dispatch={dispatch}
      orderObj={orderObj}
      dispatchPage={dispatchPage}
      jStoreOptions={jStoreOptions}
    />
  )
}

export default DispatcherPageListWrapper
